/**
 * Deriving whether a backup job is actually protecting anything.
 *
 * This is the one piece of real logic in the portal, and the one most worth
 * getting right. A dashboard that says "healthy" over a job which last
 * succeeded four months ago is worse than no dashboard: it converts an
 * unnoticed problem into a *confidently* unnoticed one.
 */

import type { BackupJob, Snapshot, UnixSeconds } from "./types";

export type JobHealth = "healthy" | "incomplete" | "stale" | "failing" | "never-run";

export interface HealthAssessment {
  readonly health: JobHealth;
  /** One sentence, suitable for showing directly to a user. */
  readonly reason: string;
  /** When this job last completed successfully. */
  readonly lastSuccessUtc: UnixSeconds | null;
  /** The most recent run of any outcome. */
  readonly lastRunUtc: UnixSeconds | null;
}

/**
 * Assumed cadence for a job that has not declared one.
 *
 * Daily is the common case for a personal backup, and assuming it means a job
 * that silently stopped a week ago is reported rather than excused.
 */
export const DEFAULT_INTERVAL_HOURS = 24;

/**
 * How far past its expected interval a job may drift before it counts as stale.
 *
 * Some slack is necessary — a laptop that was closed overnight is not a
 * failure — but too much slack defeats the point of the check.
 */
export const STALENESS_TOLERANCE = 2;

const HOUR_SECONDS = 3600;

/**
 * How long a file may be carried over before it counts against health.
 *
 * Matches the desktop's own escalation window (`missed_alerts.rs`): a file one
 * program held open for a night is noise, and a file no run has read in a week
 * is not. Kept in step with `ESCALATE_AFTER_SECONDS` there.
 */
const ESCALATION_WINDOW_SECONDS = 7 * 24 * HOUR_SECONDS;

/**
 * Of a run's Missed files, how many have an earlier copy to fall back to.
 *
 * The subset that is genuinely "carried over" rather than "never captured" —
 * absent counts read as zero, matching every other reading of these fields.
 */
function carriedOverCount(run: Snapshot): number {
  return (run.missedFiles ?? 0) - (run.neverCapturedFiles ?? 0);
}

/**
 * Assess a job from its runs.
 *
 * `snapshots` may be in any order and may include runs from other jobs; only
 * those belonging to `job` are considered.
 */
export function assessJob(
  job: BackupJob,
  snapshots: readonly Snapshot[],
  nowUtc: UnixSeconds,
): HealthAssessment {
  // `slice().sort()` rather than `toSorted`: this package is consumed as
  // TypeScript source, so its syntax becomes a floor on every consumer's `lib`
  // setting. Staying conservative keeps that floor low.
  const mine = snapshots
    .filter((s) => s.jobId === job.id && s.machineId === job.machineId)
    .slice()
    .sort((a, b) => b.finishedUtc - a.finishedUtc);

  const lastRun = mine[0] ?? null;
  const lastSuccess = mine.find((s) => s.status === "complete") ?? null;

  if (!lastRun) {
    return {
      health: "never-run",
      reason: "This job has never run, so nothing is backed up yet.",
      lastSuccessUtc: null,
      lastRunUtc: null,
    };
  }

  // A failure since the last success is the loudest signal available, and it
  // takes priority over recency: a job failing every hour is not "healthy"
  // merely because it ran recently.
  if (lastRun.status !== "complete") {
    return {
      health: "failing",
      reason:
        lastRun.status === "cancelled"
          ? "The most recent run was stopped before it finished."
          : "The most recent run failed.",
      lastSuccessUtc: lastSuccess?.finishedUtc ?? null,
      lastRunUtc: lastRun.finishedUtc,
    };
  }

  // A run that succeeded is not the same as a run that captured everything.
  // Missed-ness travels beside the status as counts (#370), so a plan can
  // report success every night while quietly never holding a copy of one
  // file. That is not "failing" — 99.9% of files landing every night is a
  // job that works — but it is not "healthy" either.
  const neverCaptured = lastRun.neverCapturedFiles ?? 0;
  const carriedOver = carriedOverCount(lastRun);

  // Never captured means no copy of the file exists anywhere, so it is
  // reported the moment it is seen rather than waiting out the window below.
  if (neverCaptured > 0) {
    return {
      health: "incomplete",
      reason:
        neverCaptured === 1
          ? "1 file has never been backed up, so no copy of it exists anywhere."
          : `${neverCaptured.toLocaleString()} files have never been backed up, so no copy of them exists anywhere.`,
      lastSuccessUtc: lastRun.finishedUtc,
      lastRunUtc: lastRun.finishedUtc,
    };
  }

  // A carried-over file only counts once it has gone unread for the whole
  // escalation window — walking backward through completed runs (`lastRun`
  // itself is the first of them) while every one still carried it over. A
  // run that failed or was cancelled read nothing, so it is invisible here
  // and neither extends nor breaks the streak.
  if (carriedOver > 0) {
    let missedSinceUtc = lastRun.finishedUtc;
    for (const run of mine.filter((s) => s.status === "complete").slice(1)) {
      if (carriedOverCount(run) <= 0) break;
      missedSinceUtc = run.finishedUtc;
    }

    if (lastRun.finishedUtc - missedSinceUtc >= ESCALATION_WINDOW_SECONDS) {
      return {
        health: "incomplete",
        reason:
          carriedOver === 1
            ? `1 file has not been backed up in ${describeAge(lastRun.finishedUtc - missedSinceUtc)}; the vault still holds an older copy.`
            : `${carriedOver.toLocaleString()} files have not been backed up in ${describeAge(lastRun.finishedUtc - missedSinceUtc)}; the vault still holds older copies.`,
        lastSuccessUtc: lastRun.finishedUtc,
        lastRunUtc: lastRun.finishedUtc,
      };
    }
  }

  const intervalHours = job.expectedIntervalHours ?? DEFAULT_INTERVAL_HOURS;
  const allowedSeconds = intervalHours * STALENESS_TOLERANCE * HOUR_SECONDS;
  const age = nowUtc - lastRun.finishedUtc;

  if (age > allowedSeconds) {
    return {
      health: "stale",
      reason: `The last successful backup was ${describeAge(age)} ago, longer than this job's schedule allows.`,
      lastSuccessUtc: lastRun.finishedUtc,
      lastRunUtc: lastRun.finishedUtc,
    };
  }

  return {
    health: "healthy",
    reason: `Last backed up ${describeAge(age)} ago.`,
    lastSuccessUtc: lastRun.finishedUtc,
    lastRunUtc: lastRun.finishedUtc,
  };
}

/** The worst health among several jobs, for a machine- or fleet-level badge. */
export function worstHealth(assessments: readonly HealthAssessment[]): JobHealth {
  const order: readonly JobHealth[] = ["failing", "never-run", "incomplete", "stale", "healthy"];
  for (const health of order) {
    if (assessments.some((a) => a.health === health)) return health;
  }
  return "healthy";
}

/** Whether a health state should draw the eye. */
export function needsAttention(health: JobHealth): boolean {
  return health !== "healthy";
}

/** A coarse, human description of an age in seconds. */
export function describeAge(seconds: number): string {
  if (seconds < 0) return "moments";
  if (seconds < 90) return "moments";

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"}`;

  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"}`;

  const days = Math.round(hours / 24);
  if (days < 60) return `${days} day${days === 1 ? "" : "s"}`;

  const months = Math.round(days / 30);
  return `${months} month${months === 1 ? "" : "s"}`;
}
